import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n/I18nContext';

const TIER_KEYS = ['free', 'pro', 'firm'];

export default function Pricing() {
  const { t, raw } = useI18n();
  const [openFaq, setOpenFaq] = useState(0);
  const [yearly, setYearly] = useState(false);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-16">
      <section className="text-center">
        <p className="landing-eyebrow">{t('pricing.eyebrow')}</p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight md:text-5xl">{t('pricing.title')}</h1>
        <p className="mx-auto mt-3 max-w-[48ch] text-base text-slate-600">{t('pricing.subtitle')}</p>

        {/* 按年 / 按月切换 */}
        <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white p-1">
          <button
            type="button"
            onClick={() => setYearly(false)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
              !yearly ? 'bg-coverfy-accent text-white' : 'text-slate-600'
            }`}
          >
            {t('pricing.monthly').replace('/', '')}
          </button>
          <button
            type="button"
            onClick={() => setYearly(true)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
              yearly ? 'bg-coverfy-accent text-white' : 'text-slate-600'
            }`}
          >
            {t('pricing.yearly').replace('/', '')}
            <span className="ml-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[0.6rem] font-semibold text-emerald-700">
              {t('pricing.save')}
            </span>
          </button>
        </div>
      </section>

      <section className="landing-section-gap grid gap-4 md:grid-cols-3">
        {TIER_KEYS.map((key) => {
          const tier = raw(`pricing.${key}`);
          const featured = !!tier.featured;
          return (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.3 }}
              className={`bezel-outer p-3 ${featured ? 'md:-translate-y-2 md:shadow-diffuse-lg' : ''}`}
            >
              <div className={`bezel-inner flex h-full flex-col p-6 ${featured ? 'border-t-4 border-t-coverfy-accent' : ''}`}>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold tracking-tight">{tier.name}</h3>
                  {featured && (
                    <span className="rounded-full bg-coverfy-accent/10 px-2.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-widest text-coverfy-accent">
                      {tier.featured}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-slate-500">{tier.tagline}</p>

                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-4xl font-semibold tracking-tight">
                    {yearly ? tier.yearlyPrice || tier.price : tier.price}
                  </span>
                  <span className="text-sm text-slate-500">
                    {yearly ? t('pricing.yearly') : t('pricing.monthly')}
                  </span>
                </div>
                {yearly && tier.yearlyPrice && tier.yearlyPrice !== tier.price && (
                  <p className="mt-1 text-xs text-slate-400 line-through">{tier.price} {t('pricing.monthly')}</p>
                )}

                <ul className="mt-5 flex-1 space-y-2">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-coverfy-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/apply?plan=${key}`}
                  className={`mt-6 inline-block rounded-full px-5 py-2.5 text-center text-sm font-medium active:scale-[0.98] ${
                    featured
                      ? 'bg-coverfy-accent text-white'
                      : 'border border-coverfy-accent text-coverfy-accent'
                  }`}
                >
                  {tier.cta}
                </Link>
              </div>
            </motion.div>
          );
        })}
      </section>

      <p className="landing-section-gap text-center text-xs text-slate-500">{t('pricing.aside')}</p>

      {/* 对比表 */}
      <section className="landing-section-gap">
        <h2 className="text-center text-2xl font-semibold tracking-tight md:text-3xl">
          {t('pricing.compare.title')}
        </h2>
        <div className="mx-auto mt-6 max-w-4xl overflow-x-auto rounded-3xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="px-4 py-3 text-left font-medium text-slate-500">{t('pricing.compare.title')}</th>
                {TIER_KEYS.map((k) => (
                  <th key={k} className="px-4 py-3 text-center font-semibold">
                    {t(`pricing.${k}.name`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {raw('pricing.compare.rows').map((row, i) => (
                <tr key={row.label} className={i % 2 ? 'bg-slate-50/60' : ''}>
                  <td className="px-4 py-3 font-medium text-slate-700">{row.label}</td>
                  {row.values.map((v, j) => (
                    <td key={j} className="px-4 py-3 text-center text-slate-600">
                      {v === '✓' ? (
                        <span className="text-coverfy-accent">✓</span>
                      ) : v === '—' ? (
                        <span className="text-slate-300">—</span>
                      ) : (
                        v
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="landing-section-gap">
        <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">FAQ</h2>
        <div className="mt-6 divide-y divide-slate-200 rounded-3xl border border-slate-200 bg-white">
          {raw('pricing.faq').map((item, i) => {
            const isOpen = openFaq === i;
            return (
              <button
                key={item.q}
                type="button"
                onClick={() => setOpenFaq(isOpen ? -1 : i)}
                className="block w-full px-6 py-4 text-left"
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm font-semibold">{item.q}</span>
                  <svg
                    className={`h-4 w-4 flex-shrink-0 text-slate-400 transition ${isOpen ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {isOpen && <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.a}</p>}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
